/**
 * Fetches every tour in the system for admin accounts.
 *
 * Backend: standalone Cloud Function `adminGetAllTours` deployed to id3d-prod.
 * Auth: Firebase ID token sent as `Authorization: Bearer <token>`. The
 * function verifies the token via firebase-admin and checks the email
 * against its own allowlist before returning data.
 */

import { getAuth } from 'firebase/auth';

import { Tour } from 'app/types';

import { getThumbnailUrlFromStorage } from './getThumbnailUrlFromStorage';

export async function getAllToursAdmin(): Promise<Tour[]> {
  const endpoint = process.env.REACT_APP_ADMIN_TOURS_URL;
  if (!endpoint) {
    throw new Error('REACT_APP_ADMIN_TOURS_URL is not configured');
  }

  const auth = getAuth();
  const idToken = await auth.currentUser?.getIdToken();
  if (!idToken) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });

  if (!response.ok) {
    let message = '';
    try {
      const body = await response.json();
      message = body?.error || body?.message || '';
    } catch {
      /* ignore */
    }
    throw new Error(
      message || `admin tours endpoint returned ${response.status}`,
    );
  }

  const payload = await response.json();
  const tours = Array.isArray(payload?.tours) ? (payload.tours as Tour[]) : [];

  // The function returns Storage paths in thumbnailUrl; resolve to signed
  // download URLs here in parallel. (Server-side signing required IAM perms
  // and was OOM-ing on bulk; client-side is the safer path until we revisit.)
  return Promise.all(
    tours.map(async (tour) => {
      if (!tour.thumbnailUrl) return tour;
      try {
        const url = await getThumbnailUrlFromStorage(tour.thumbnailUrl);
        return { ...tour, thumbnailUrl: url };
      } catch {
        return tour;
      }
    }),
  );
}
